/**
 * Forgot-password page — two modes:
 *
 *   1. **Request mode** (no `?token=…` in URL): user enters email,
 *      we POST `/auth/password-reset-request`, and ALWAYS show the same
 *      generic "Check your email" message. We never reveal whether the
 *      email exists, and we never expose the reset token to the browser
 *      (the email contains the only copy of the link).
 *
 *   2. **Reset mode** (URL has `?token=…`, reached via email link):
 *      user sets a new password, we POST `/auth/password-reset`. On
 *      success the user is sent to `/logg-inn`. Invalid / used / expired
 *      tokens surface a clean error and prompt the user to request a
 *      fresh link.
 *
 * No dev backdoors. The frontend has no way to peek at a token.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Mail, Check, KeyRound, ArrowLeft } from "lucide-react";
import { PANEL_CLS, PRIMARY_BTN_CLS, PAGE_EYEBROW_CLS } from "@/components/premium";

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";
  // mode is fixed at mount based on URL state — we don't switch modes
  // mid-flight to keep the user's mental model simple.
  const isResetMode = !!tokenFromUrl;

  // Request mode state
  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);

  // Reset mode state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);

  const [loading, setLoading] = useState(false);

  // Reset mode token validation happens server-side on submit. We don't
  // pre-validate to avoid leaking which tokens exist via timing or status
  // codes (a malicious client could otherwise enumerate valid tokens).
  useEffect(() => { /* no-op — kept for future client-side helpers */ }, [tokenFromUrl]);

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Skriv inn e-post");
    setLoading(true);
    try {
      await api.post("/auth/password-reset-request", { email });
      // Always show the same generic success — never reveal whether the
      // email exists or not. The backend mirrors this behaviour.
      setRequestSent(true);
    } catch (err) {
      // Network / server errors only. 200 responses always include the
      // generic message regardless of whether the email exists.
      toast.error(err?.response?.data?.detail || "Noe gikk galt — prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("Passord må være minst 8 tegn");
    if (newPassword !== confirmPassword) return toast.error("Passordene må være like");
    setLoading(true);
    try {
      await api.post("/auth/password-reset", { token: tokenFromUrl, new_password: newPassword });
      setResetDone(true);
      toast.success("Passordet er oppdatert. Du kan logge inn.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string"
        ? detail
        : "Kunne ikke tilbakestille passordet. Lenken kan være utløpt eller allerede brukt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <SEO title="Glemt passord — ClientFlow" path="/glemt-passord" noindex />
      <FloatingThemeToggle />
      {/* Hero glow behind the card — same vocabulary as Dashboard. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-emerald-500/[0.05] blur-3xl" />
      <Card className={`${PANEL_CLS} relative w-full max-w-md anim-in overflow-hidden`} data-testid="forgot-password-card">
        <div className="pointer-events-none absolute -top-20 -right-20 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl" />
        <CardContent className="relative p-8">
          <Link to="/logg-inn" className="text-sm text-muted-foreground hover:text-emerald-500 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Tilbake til innlogging
          </Link>

          {/* === Reset mode (lands here from the email link) === */}
          {isResetMode ? (
            <>
              <div className="mt-6 inline-flex w-12 h-12 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-500 items-center justify-center shadow-[0_8px_30px_-10px_rgba(16,185,129,0.5)]">
                <KeyRound className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <p className={`${PAGE_EYEBROW_CLS} mt-4`}>Tilbakestill passord</p>
              <h1 className="mt-1.5 font-heading text-3xl font-bold tracking-tight">Sett nytt passord</h1>
              <p className="text-muted-foreground mt-2">Velg et nytt passord på minst 8 tegn.</p>

              {resetDone ? (
                <div
                  className="mt-8 p-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] text-sm"
                  data-testid="reset-success"
                >
                  <div className="flex items-center gap-2 text-emerald-500 font-semibold">
                    <Check className="w-4 h-4" strokeWidth={2.25} /> Passordet er oppdatert.
                  </div>
                  <p className="mt-2 text-foreground/80">Du kan nå logge inn med det nye passordet.</p>
                  <Link
                    to="/logg-inn"
                    className="mt-4 inline-block font-semibold text-emerald-500 hover:underline"
                    data-testid="back-to-login-link"
                  >
                    → Til innlogging
                  </Link>
                </div>
              ) : (
                <form onSubmit={submitReset} className="mt-8 space-y-4">
                  <div>
                    <Label htmlFor="newpw">Nytt passord</Label>
                    <PasswordInput
                      id="newpw"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="rounded-xl mt-1.5 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40"
                      autoComplete="new-password"
                      data-testid="forgot-newpw-input"
                      toggleTestId="forgot-newpw-toggle"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmpw">Bekreft nytt passord</Label>
                    <PasswordInput
                      id="confirmpw"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="rounded-xl mt-1.5 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40"
                      autoComplete="new-password"
                      data-testid="forgot-confirmpw-input"
                      toggleTestId="forgot-confirmpw-toggle"
                    />
                  </div>
                  <Button
                    type="submit"
                    className={`w-full ${PRIMARY_BTN_CLS} h-11`}
                    disabled={loading}
                    data-testid="forgot-reset-btn"
                  >
                    {loading ? "Oppdaterer…" : "Sett nytt passord"}
                  </Button>
                </form>
              )}
            </>
          ) : (
            /* === Request mode (entry point) === */
            <>
              <div className="mt-6 inline-flex w-12 h-12 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-500 items-center justify-center shadow-[0_8px_30px_-10px_rgba(16,185,129,0.5)]">
                <Mail className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <p className={`${PAGE_EYEBROW_CLS} mt-4`}>Glemt passord</p>
              <h1 className="mt-1.5 font-heading text-3xl font-bold tracking-tight">Tilbakestill passord</h1>
              <p className="text-muted-foreground mt-2">
                Skriv inn e-posten din, så sender vi deg en sikker tilbakestillings-lenke.
              </p>

              {requestSent ? (
                <div
                  className="mt-8 p-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] text-sm"
                  data-testid="request-sent"
                >
                  <div className="flex items-center gap-2 text-emerald-500 font-semibold">
                    <Check className="w-4 h-4" strokeWidth={2.25} /> Sjekk e-posten din
                  </div>
                  <p className="mt-2 text-foreground/80">
                    Hvis kontoen finnes, har vi sendt en lenke for å tilbakestille passordet. Lenken er gyldig i 45 minutter.
                  </p>
                  <p className="mt-2 text-muted-foreground text-xs">
                    Ikke fått e-post? Sjekk søppelpost-mappen eller prøv igjen om noen minutter.
                  </p>
                </div>
              ) : (
                <form onSubmit={requestReset} className="mt-8 space-y-4">
                  <div>
                    <Label htmlFor="email">E-post</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl mt-1.5 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40"
                      autoComplete="email"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className={`w-full ${PRIMARY_BTN_CLS} h-11`}
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? "Sender…" : "Send tilbakestillings-lenke"}
                  </Button>
                </form>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
